import { Icon, IconSize, IconVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { LoadingForType, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { Checkbox, Image, RadioGroup, Spacer } from "@nextui-org/react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import AlternateArrow from "/Assets/alternateArrow.png";
import AlternateArrowDark from "/Assets/alternateArrowDark.png";
import MaskIcon from "/Assets/mask.png";
import { Col, Grid, Row, SpicyButton, SpicyInput, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { CHOOSE_PERSONA_LOCAL_STORAGE_KEY, ROUTES } from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { PREFERRED_VOICE_ACTIONS } from "@/pages/Chat/components/voices/constant";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import NavigateBackButton from "../../common/NavigateBackButton";
import PersonaCard from "./PersonaCard";
import { ChoosePersonaProps } from "./types";

export default function ChoosePersona(props: ChoosePersonaProps) {
  const {
    character,
    setIsChoosePersona,
    selectedPersona,
    personas,
    setIsChangePersona,
    isDoNotShow,
    setSelectedPersonaIds,
    onUserConfirmChangePersonaInConversation,
    conversationId,
    loading,
    isChangePersona,
    onUserConfirmSelectedPersonaToChat,
    onNewConversation,
    isNewConversation,
    setIsNewConversation,
    resetSelectedPersonaIds,
    defaultPersonaId,
    selectedPersonaIds,
    handlePreferredVoiceActions,
  } = props;
  const { t } = useTranslation();
  const { name } = useUserInfo();
  const { blurNSFW } = useUserSettings();
  const { pagePath } = useLocalizedLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(selectedPersona?.avatar_url);
  const isStartChatLoading = loading?.loadingType === LoadingForType.changePersonaToChat && loading.isLoading;
  const filteredPersonas = personas?.filter((d) => d?.name.toLowerCase().startsWith(searchQuery?.trim()?.toLowerCase()));
  const { isMobile } = useMedia();
  const isCharacterAvatarBlurred = blurNSFW && character?.avatar_is_nsfw;

  const handleDoNotShow = (value: boolean) => {
    typedLocalStorageService.setItem(CHOOSE_PERSONA_LOCAL_STORAGE_KEY, String(value));
  };

  const { theme } = useTheme();
  const clearInput = () => {
    setSearchQuery("");
  };

  useEffect(() => {
    if (isNewConversation || !conversationId) {
      setSelectedPersonaIds([selectedPersona?.id ?? ""]);
    }
    return () => {
      if (isNewConversation) {
        setIsNewConversation(false);
      }
      resetSelectedPersonaIds();
    };
  }, []);

  const handleStartChat = async () => {
    if (isNewConversation || !conversationId) {
      onNewConversation();
      onUserConfirmSelectedPersonaToChat();
    } else {
      // only calling update persona api if persona is changed by user.
      if (selectedPersona && !selectedPersonaIds.includes(selectedPersona.id)) {
        await onUserConfirmChangePersonaInConversation(conversationId);
      }
    }
    setIsChangePersona(false);
    setIsChoosePersona(false);
    handlePreferredVoiceActions({ action: PREFERRED_VOICE_ACTIONS.Set_VoiceId, voiceId: "" });
  };

  return (
    <>
      <Col className="*:pl-0 p-4">
        <NavigateBackButton isChangePersona={isChangePersona} setIsChangePersona={setIsChangePersona} />
        <Row className="justify-between">
          <h3 className="tracking-tighter text-[24px] font-semibold pl-3">
            <Trans i18nKey="chat:modal.choosePersona.title" values={{ characterName: character?.name }} />
          </h3>
        </Row>

        <Row style={undefined} justify="between" align="center" gap={1} className="flex-wrap">
          <Grid className="max-w-[700px] grow">
            <SpicyInput
              value={searchQuery}
              variant="primary"
              color="primary"
              placeholder={t("chat:modal.choosePersona.search.placeholder")}
              onValueChange={setSearchQuery}
              isClearable
              onClear={clearInput}
              aria-labelledby="search-characters"
            />
          </Grid>

          <LocalizedLink
            className="cursor-pointer"
            to={ROUTES.personas}
            state={{ path: pagePath, characterName: character?.name }}
            aria-label="manage-personas"
          >
            <Text mNone={undefined} variant={"lg"} type={"primary"}>
              <Trans i18nKey="chat:modal.choosePersona.cta.managePersonas" />
            </Text>
          </LocalizedLink>
        </Row>

        <Spacer y={10} />

        <Row
          style={undefined}
          align={"center"}
          justify={"center"}
          className={"grid grid-cols-3 place-items-center gap-[5rem] max-sm:gap-[4vw] mx-auto " + (isMobile && "!px-2")}
        >
          <Grid className="relative">
            {selectedAvatar ? (
              <Image
                removeWrapper
                loading="lazy"
                className="object-cover rounded-[10px] z-0 max-w-full max-[450px]:h-[27vw] opacity-100"
                src={getAvatarUrl(selectedAvatar, { preset: AvatarPreset.AVATAR_128X128 }) as string}
                alt={character?.name}
                width={120}
                height={120}
              />
            ) : (
              <div className="bg-zinc-300 text-gray-600 w-[120px] rounded-2xl flex justify-center items-center max-w-full h-[120px]">
                <Icon icon={IconVariants.USER} size={IconSize.XLARGE} />
              </div>
            )}
            <Grid className="rounded-full absolute -top-1 -right-1 bg-gray-100 dark:bg-gray-900 p-1 overflow-hidden">
              <Image removeWrapper src={MaskIcon} width={20} radius="none" />
            </Grid>
          </Grid>
          <Image src={theme === "dark" ? AlternateArrow : AlternateArrowDark} width={70} />
          <Grid className="">
            <Image
              removeWrapper
              loading="lazy"
              className={cn("object-cover rounded-[10px] z-0 max-w-full max-[450px]:h-[27vw] opacity-100", isCharacterAvatarBlurred && "blur-md")}
              src={
                getAvatarUrl(character?.avatar_url ?? "", {
                  preset: AvatarPreset.AVATAR_128X128,
                }) as string
              }
              alt={name ?? ""}
              width={120}
              height={120}
            />
          </Grid>
        </Row>
        <Spacer y={10} />
        <Col>
          <RadioGroup
            classNames={{
              wrapper: `grid max-sm:grid-cols-1 place-items-center ${isMobile ? "gap-2" : `gap-10 ${personas.length === 1 ? "grid-cols-1 mx-auto" : personas.length === 2 ? "grid-cols-2 mx-auto" : "grid-cols-[repeat(auto-fit,minmax(400px,1fr))]"}`}`,
            }}
            defaultValue={selectedPersona?.id}
          >
            {!filteredPersonas?.length ? (
              <Text>
                <Trans i18nKey="chat:modal.choosePersona.noPersonaFound" />
              </Text>
            ) : (
              filteredPersonas
                ?.sort((a, b) => (a.id === defaultPersonaId ? -1 : b.id === defaultPersonaId ? 1 : 0))
                ?.map((data, idx) => (
                  <PersonaCard
                    key={idx}
                    data={data}
                    setSelectedAvatar={setSelectedAvatar}
                    setSelectedPersonaIds={setSelectedPersonaIds}
                    isMobile={isMobile}
                    defaultPersonaId={defaultPersonaId ?? ""}
                  />
                ))
            )}
          </RadioGroup>
        </Col>
        <Spacer y={10} />

        <Col className="justify-end items-end sticky bottom-0 pt-6 pb-8 bg-white dark:bg-black z-10 gap-4 w-[100% - ]">
          <Checkbox defaultSelected={isDoNotShow} onValueChange={(value: boolean) => handleDoNotShow(value)} className="pr-4">
            <Trans i18nKey="chat:modal.choosePersona.checkboxLabel" />
          </Checkbox>

          <SpicyButton variant="primary_large" color="primary_strong" onPress={handleStartChat} isLoading={isStartChatLoading}>
            <Text b type="white">
              <Trans i18nKey="chat:modal.choosePersona.cta.chatWith" values={{ characterName: character?.name }} />
            </Text>
          </SpicyButton>
        </Col>
      </Col>
    </>
  );
}
